import React, { useState, useEffect, useRef } from 'react';
import DeckSelector from './DeckSelector';
import GameModeSelector from './GameModeSelector';
import GameSelector from './GameSelector';
import WhichingHourGame from './WhichingHourGame';
import JourneyGame from './JourneyGame';
import CategorySelector from './CategorySelector';
import DeckGallery from './DeckGallery';
import GameHeader from './game/GameHeader';
import ScoreBoard from './game/ScoreBoard';
import GameStatus from './game/GameStatus';
import TrickDisplay from './game/TrickDisplay';
import PlayerHand from './game/PlayerHand';
import GameControls from './game/GameControls';
import { dealCards, GAME_STATES, PLAYERS, DECK_INFO, DECK_TYPES, GAME_MODES, CATEGORIES } from '../mock/gameData';
import { selectAICard, getAIDifficultyInfo } from '../mock/aiLogic';
import { useToast } from '../hooks/use-toast';

const CityCardGame = ({ user, onLogout, onViewLeaderboard, onViewStats, onNicknameChange, challengeData, onChallengeComplete }) => {
  const [gameState, setGameState] = useState(GAME_STATES.GAME_SELECTOR);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [isDailyChallenge, setIsDailyChallenge] = useState(false);
  const [gameMode, setGameMode] = useState(null);
  const [aiDifficulty, setAIDifficulty] = useState(null);
  const [player1Cards, setPlayer1Cards] = useState([]);
  const [player2Cards, setPlayer2Cards] = useState([]);
  const [player1Tricks, setPlayer1Tricks] = useState(0);
  const [player2Tricks, setPlayer2Tricks] = useState(0);
  const [currentTrick, setCurrentTrick] = useState({ player1Card: null, player2Card: null });
  const [currentPlayer, setCurrentPlayer] = useState(PLAYERS.PLAYER1);
  const [selectedCard, setSelectedCard] = useState(null);
  const [winner, setWinner] = useState(null);
  const [totalGames, setTotalGames] = useState(0);
  const [player1Wins, setPlayer1Wins] = useState(0);
  const [player2Wins, setPlayer2Wins] = useState(0);
  const aiProcessingRef = useRef(false); // Lock to prevent duplicate AI actions
  const { toast } = useToast();

  // Handle challenge data - auto-start game with challenge deck
  useEffect(() => {
    if (challengeData) {
      setGameState(GAME_STATES.WHICHING_HOUR);
    }
  }, [challengeData]);

  const handleGameSelect = (gameType, preselectedDeck = null) => {
    if (gameType === 'cards') {
      setGameState(GAME_STATES.CATEGORY_SELECTION);
    } else if (gameType === 'whiching') {
      if (preselectedDeck) {
        setSelectedDeck(preselectedDeck);
      }
      setIsDailyChallenge(false);
      setGameState(GAME_STATES.WHICHING_HOUR);
    } else if (gameType === 'daily_challenge') {
      if (preselectedDeck) {
        setSelectedDeck(preselectedDeck);
      }
      setIsDailyChallenge(true);
      setGameState(GAME_STATES.WHICHING_HOUR);
    } else if (gameType === 'journey') {
      setGameState(GAME_STATES.JOURNEY);
    }
  };

  const backToGameSelection = () => {
    setGameState(GAME_STATES.GAME_SELECTOR);
    if (onChallengeComplete) onChallengeComplete();
  };

  const handleCategorySelect = (category) => {
    if (category === CATEGORIES.RANDOM) {
      // Pick a random deck from all decks
      const allDecks = Object.keys(DECK_INFO);
      const randomDeck = allDecks[Math.floor(Math.random() * allDecks.length)];
      setSelectedDeck(randomDeck);
      setGameState(GAME_STATES.MODE_SELECTION);
    } else {
      setSelectedCategory(category);
      setGameState(GAME_STATES.DECK_SELECTION);
    }
  };

  const backToCategorySelection = () => {
    setGameState(GAME_STATES.CATEGORY_SELECTION);
    setSelectedCategory(null);
  };

  const handleDeckSelect = (deckType) => {
    setSelectedDeck(deckType);
    setGameState(GAME_STATES.MODE_SELECTION);
  };

  const handleModeSelect = (mode, difficulty = null) => {
    if (mode === 'gallery') {
      setGameState(GAME_STATES.GALLERY_VIEW);
      return;
    }
    setGameMode(mode);
    setAIDifficulty(difficulty);
    setGameState(GAME_STATES.WAITING);
  };

  const backToDeckSelection = () => {
    setGameState(GAME_STATES.DECK_SELECTION);
    setSelectedDeck(null);
    setGameMode(null);
    setAIDifficulty(null);
    resetGameState();
  };

  const backToModeSelection = () => {
    setGameState(GAME_STATES.MODE_SELECTION);
    setGameMode(null);
    setAIDifficulty(null);
    resetGameState();
  };

  const resetGameState = () => {
    setPlayer1Cards([]);
    setPlayer2Cards([]);
    setPlayer1Tricks(0);
    setPlayer2Tricks(0);
    setCurrentTrick({ player1Card: null, player2Card: null });
    setCurrentPlayer(PLAYERS.PLAYER1);
    setSelectedCard(null);
    setWinner(null);
  };

  const startNewGame = () => {
    const { player1Cards: p1Cards, player2Cards: p2Cards } = dealCards(selectedDeck);
    setPlayer1Cards(p1Cards);
    setPlayer2Cards(p2Cards);
    setPlayer1Tricks(0);
    setPlayer2Tricks(0);
    setCurrentTrick({ player1Card: null, player2Card: null });
    setCurrentPlayer(PLAYERS.PLAYER1);
    setSelectedCard(null);
    setWinner(null);
    setGameState(GAME_STATES.PLAYER1_TURN);
    toast({
      title: "New Game Started!",
      description: gameMode === GAME_MODES.HUMAN_VS_COMPUTER ? "Your turn to play a card" : "Player 1's turn to play a card"
    });
  };

  // Auto-play logic for when only one card remains
  const checkForAutoPlay = () => {
    if (currentPlayer === PLAYERS.PLAYER1 && player1Cards.length === 1) {
      // Player 1 has only one card - auto play it
      setTimeout(() => {
        playCard(player1Cards[0]);
        toast({
          title: "Auto-played your last card",
          description: `${player1Cards[0].name} - no choice remaining!`
        });
      }, 1000);
    } else if (gameMode === GAME_MODES.HUMAN_VS_HUMAN && currentPlayer === PLAYERS.PLAYER2 && player2Cards.length === 1) {
      // Player 2 has only one card in human vs human - auto play it
      setTimeout(() => {
        playCard(player2Cards[0]);
        toast({
          title: "Auto-played Player 2's last card",
          description: `${player2Cards[0].name} - no choice remaining!`
        });
      }, 1000);
    }
  };

  // Check for auto-play whenever game state changes
  useEffect(() => {
    if (gameState === GAME_STATES.PLAYER1_TURN || gameState === GAME_STATES.PLAYER2_TURN) {
      checkForAutoPlay();
    }
  }, [gameState, player1Cards.length, player2Cards.length]);

  const playCard = (card, isAI = false) => {
    // Prevent playing cards if game is over
    if (gameState === GAME_STATES.GAME_OVER) {
      console.log('Attempted to play card after game over - ignoring');
      return;
    }
    
    console.log(`Playing card: ${card.name}, isAI: ${isAI}, currentPlayer: ${currentPlayer}`);
    
    if (currentPlayer === PLAYERS.PLAYER1) {
      setCurrentTrick(prev => ({ ...prev, player1Card: card }));
      setPlayer1Cards(prev => prev.filter(c => c.id !== card.id));
      setSelectedCard(null);
      
      // Check if this is responding to AI's lead
      if (currentTrick.player2Card) {
        // Player is responding to AI's lead
        setGameState(GAME_STATES.TRICK_REVEAL);
        const isLastTrick = player1Cards.length === 1 && player2Cards.length === 1;
        setTimeout(() => {
          resolveTrick(card, currentTrick.player2Card, isLastTrick);
        }, 1500);
      } else {
        // Player is leading, AI will respond
        setCurrentPlayer(PLAYERS.PLAYER2);
        if (gameMode === GAME_MODES.HUMAN_VS_COMPUTER) {
          setTimeout(() => {
            handleAITurn(card);
          }, 500);
        } else {
          setGameState(GAME_STATES.PLAYER2_TURN);
        }
      }
    } else {
      setCurrentTrick(prev => ({ ...prev, player2Card: card }));
      setPlayer2Cards(prev => prev.filter(c => c.id !== card.id));
      setGameState(GAME_STATES.TRICK_REVEAL);
      setSelectedCard(null);
      
      if (isAI) {
        toast({
          title: `AI plays ${card.name}`,
          description: "Resolving trick..."
        });
      }
      
      // Reveal trick result after a short delay
      const isLastTrick = player1Cards.length === 1 && player2Cards.length === 1;
      setTimeout(() => {
        resolveTrick(currentTrick.player1Card, card, isLastTrick);
      }, 1500);
    }
  };

  const handleAITurn = (playerCard) => {
    console.log('AI responding to player card:', playerCard);
    
    if (aiProcessingRef.current) {
      console.log('AI already processing (ref lock), skipping');
      return;
    }
    
    aiProcessingRef.current = true;
    setGameState(GAME_STATES.AI_THINKING);
    
    try {
      const aiCard = selectAICard(
        player2Cards, 
        playerCard, 
        aiDifficulty,
        { playerTricks: player1Tricks, aiTricks: player2Tricks }
      );
      
      console.log('AI selected card:', aiCard);
      if (aiCard) {
        if (!player2Cards.find(c => c.id === aiCard.id)) {
          console.error('AI tried to play card not in hand:', aiCard);
          aiProcessingRef.current = false;
          return;
        }
        
        setCurrentTrick(prev => ({ ...prev, player2Card: aiCard }));
        setPlayer2Cards(prev => prev.filter(c => c.id !== aiCard.id));
        setGameState(GAME_STATES.TRICK_REVEAL);
        
        toast({
          title: `AI plays ${aiCard.name}`,
          description: "Resolving trick..."
        });
        
        const isLastTrick = player1Cards.length === 1 && player2Cards.length === 1;
        setTimeout(() => {
          resolveTrick(playerCard, aiCard, isLastTrick);
        }, 1500);
      } else {
        console.error('AI could not select a card');
        if (player2Cards.length > 0) {
          const fallbackCard = player2Cards[0];
          setCurrentTrick(prev => ({ ...prev, player2Card: fallbackCard }));
          setPlayer2Cards(prev => prev.filter(c => c.id !== fallbackCard.id));
          setGameState(GAME_STATES.TRICK_REVEAL);
          
          const isLastTrick = player1Cards.length === 1 && player2Cards.length === 1;
          setTimeout(() => {
            resolveTrick(playerCard, fallbackCard, isLastTrick);
          }, 1500);
        }
      }
    } catch (error) {
      console.error('Error in AI turn:', error);
    }
  };

  const resolveTrick = (p1Card, p2Card, isLastTrick = false) => {
    const player1Wins = p1Card.rank < p2Card.rank;
    
    if (player1Wins) {
      const newPlayer1Tricks = player1Tricks + 1;
      setPlayer1Tricks(newPlayer1Tricks);
      
      const margin = p2Card.rank - p1Card.rank;
      const isBigWin = margin > 10;
      toast({
        title: gameMode === GAME_MODES.HUMAN_VS_COMPUTER ? "You win!" : "Player 1 wins!",
        description: `${p1Card.name} beats ${p2Card.name} by ${margin}`,
        className: isBigWin ? "border-red-500 bg-red-950 text-red-100" : "",
        style: isBigWin ? { fontSize: '1.25rem' } : {}
      });
      
      if (newPlayer1Tricks >= 3) {
        setTimeout(() => {
          endGame(PLAYERS.PLAYER1);
          aiProcessingRef.current = false;
        }, 2500);
        setTimeout(() => {
          setCurrentTrick({ player1Card: null, player2Card: null });
        }, 3000);
        return;
      }
      
      aiProcessingRef.current = false;
      setCurrentPlayer(PLAYERS.PLAYER1);
    } else {
      const newPlayer2Tricks = player2Tricks + 1;
      setPlayer2Tricks(newPlayer2Tricks);
      
      const margin = p1Card.rank - p2Card.rank;
      const isBigWin = margin > 10;
      const winnerName = gameMode === GAME_MODES.HUMAN_VS_COMPUTER ? "AI" : "Player 2";
      toast({
        title: `${winnerName} wins!`,
        description: `${p2Card.name} beats ${p1Card.name} by ${margin}`,
        className: isBigWin ? "border-red-500 bg-red-950 text-red-100" : "",
        style: isBigWin ? { fontSize: '1.25rem' } : {}
      });
      
      if (newPlayer2Tricks >= 3) {
        setTimeout(() => {
          endGame(PLAYERS.PLAYER2);
          aiProcessingRef.current = false;
        }, 2500);
        setTimeout(() => {
          setCurrentTrick({ player1Card: null, player2Card: null });
        }, 3000);
        return;
      }
      
      aiProcessingRef.current = false;
      setCurrentPlayer(PLAYERS.PLAYER2);
    }

    setTimeout(() => {
      setCurrentTrick({ player1Card: null, player2Card: null });
      
      if (player1Cards.length === 1 && player2Cards.length === 1) {
        return;
      }
      
      if (player1Wins) {
        setGameState(GAME_STATES.PLAYER1_TURN);
      } else {
        if (gameMode === GAME_MODES.HUMAN_VS_COMPUTER) {
          setTimeout(() => {
            handleAILead();
          }, 500);
        } else {
          setGameState(GAME_STATES.PLAYER2_TURN);
        }
      }
    }, 2500);
  };

  const handleAILead = () => {
    console.log('AI leading next trick...');
    
    if (aiProcessingRef.current) {
      console.log('AI already processing (ref lock), skipping');
      return;
    }
    
    aiProcessingRef.current = true;
    setGameState(GAME_STATES.AI_THINKING);
    
    if (player2Cards.length === 1) {
      const lastCard = player2Cards[0];
      setCurrentTrick(prev => ({ ...prev, player2Card: lastCard }));
      setPlayer2Cards(prev => prev.filter(c => c.id !== lastCard.id));
      setCurrentPlayer(PLAYERS.PLAYER1);
      setGameState(GAME_STATES.PLAYER1_TURN);
      aiProcessingRef.current = false;
      
      toast({
        title: `AI auto-plays last card: ${lastCard.name}`,
        description: "No choice remaining - your turn to respond!"
      });
      return;
    }
    
    try {
      const aiCard = selectAICard(
        player2Cards, 
        null,
        aiDifficulty,
        { playerTricks: player1Tricks, aiTricks: player2Tricks }
      );
      
      console.log('AI leads with card:', aiCard);
      if (aiCard) {
        if (!player2Cards.find(c => c.id === aiCard.id)) {
          console.error('AI tried to lead with card not in hand:', aiCard);
          aiProcessingRef.current = false;
          return;
        }
        
        setCurrentTrick(prev => ({ ...prev, player2Card: aiCard }));
        setPlayer2Cards(prev => prev.filter(c => c.id !== aiCard.id));
        setCurrentPlayer(PLAYERS.PLAYER1);
        setGameState(GAME_STATES.PLAYER1_TURN);
        aiProcessingRef.current = false;
        
        toast({
          title: `AI leads with ${aiCard.name}`,
          description: "Your turn to respond!"
        });
      } else {
        console.error('AI could not select a card to lead');
        if (player2Cards.length > 0) {
          const fallbackCard = player2Cards[0];
          setCurrentTrick(prev => ({ ...prev, player2Card: fallbackCard }));
          setPlayer2Cards(prev => prev.filter(c => c.id !== fallbackCard.id));
          setCurrentPlayer(PLAYERS.PLAYER1);
          setGameState(GAME_STATES.PLAYER1_TURN);
          aiProcessingRef.current = false;
        }
      }
    } catch (error) {
      console.error('Error in AI lead:', error);
      aiProcessingRef.current = false;
    }
  };

  const endGame = (winningPlayer) => {
    console.log(`Game ending - winner: ${winningPlayer}`);
    
    setWinner(winningPlayer);
    setGameState(GAME_STATES.GAME_OVER);
    setCurrentPlayer(null);
    setTotalGames(prev => prev + 1);
    
    setTimeout(() => {
      setPlayer1Cards([]);
      setPlayer2Cards([]);
    }, 500);
    
    const winnerTricks = winningPlayer === PLAYERS.PLAYER1 ? player1Tricks + 1 : player2Tricks + 1;
    const loserTricks = winningPlayer === PLAYERS.PLAYER1 ? player2Tricks : player1Tricks;
    
    let victoryType = "";
    let victoryIcon = "🎉";
    
    if (loserTricks === 0) {
      victoryType = "Sweep!";
      victoryIcon = "🧹✨";
    } else if (loserTricks === 2) {
      victoryType = "Close Victory!";
      victoryIcon = "⚔️🎉";
    } else {
      victoryType = "Victory!";
      victoryIcon = "🎉";
    }
    
    if (winningPlayer === PLAYERS.PLAYER1) {
      setPlayer1Wins(prev => prev + 1);
      const winnerName = gameMode === GAME_MODES.HUMAN_VS_COMPUTER ? "You Win" : "Player 1 Wins";
      toast({
        title: `${victoryIcon} ${winnerName} - ${victoryType}`,
        description: `Final Score: ${winnerTricks}-${loserTricks}`
      });
    } else {
      setPlayer2Wins(prev => prev + 1);
      if (gameMode === GAME_MODES.HUMAN_VS_COMPUTER) {
        toast({
          title: "Hey Sad Panda, You Lost!",
          description: `Final Score: ${winnerTricks}-${loserTricks}`
        });
      } else {
        toast({
          title: `${victoryIcon} Player 2 Wins - ${victoryType}`,
          description: `Final Score: ${winnerTricks}-${loserTricks}`
        });
      }
    }
  };

  const getGameStatusText = () => {
    if (gameState === GAME_STATES.WAITING) return "Press Deal Cards to start a new game!";
    if (gameState === GAME_STATES.PLAYER1_TURN) {
      const playerName = gameMode === GAME_MODES.HUMAN_VS_COMPUTER ? "Your" : "Player 1's";
      const autoPlay = player1Cards.length === 1 ? " - Auto-playing last card..." : " - Select a card to play";
      return `${playerName} turn${autoPlay}`;
    }
    if (gameState === GAME_STATES.PLAYER2_TURN) {
      const autoPlay = player2Cards.length === 1 ? " - Auto-playing last card..." : " - Select a card to play";
      return `Player 2's turn${autoPlay}`;
    }
    if (gameState === GAME_STATES.AI_THINKING) {
      const thinking = player2Cards.length === 1 ? "AI auto-playing last card..." : "AI is thinking...";
      return thinking;
    }
    if (gameState === GAME_STATES.TRICK_REVEAL) return "Resolving trick...";
    if (gameState === GAME_STATES.GAME_OVER) {
      const winnerTricks = winner === PLAYERS.PLAYER1 ? player1Tricks : player2Tricks;
      const loserTricks = winner === PLAYERS.PLAYER1 ? player2Tricks : player1Tricks;
      
      let victoryType = "";
      if (loserTricks === 0) {
        victoryType = " - Sweep! 🧹✨";
      } else if (loserTricks === 2) {
        victoryType = " - Close Victory! ⚔️";
      }
      
      if (gameMode === GAME_MODES.HUMAN_VS_COMPUTER) {
        return winner === PLAYERS.PLAYER1 ? `You win the game!${victoryType}` : `AI wins the game!${victoryType}`;
      }
      return `${winner === PLAYERS.PLAYER1 ? 'Player 1' : 'Player 2'} wins the game!${victoryType}`;
    }
    return "";
  };

  // Show game selector (main menu)
  if (gameState === GAME_STATES.GAME_SELECTOR) {
    return (
      <GameSelector 
        onGameSelect={handleGameSelect} 
        user={user}
        onLogout={onLogout}
        onViewLeaderboard={onViewLeaderboard}
        onViewStats={onViewStats}
        onNicknameChange={onNicknameChange}
      />
    );
  }

  // Show Which'ing Hour game
  if (gameState === GAME_STATES.WHICHING_HOUR) {
    return (
      <WhichingHourGame 
        onBack={backToGameSelection} 
        user={user}
        onLogout={onLogout}
        onViewLeaderboard={onViewLeaderboard}
        onViewStats={onViewStats}
        onNicknameChange={onNicknameChange}
        challengeData={challengeData}
        initialDeck={selectedDeck}
        isDailyChallenge={isDailyChallenge}
      />
    );
  }

  // Journey Mode
  if (gameState === GAME_STATES.JOURNEY) {
    return (
      <JourneyGame
        onBack={backToGameSelection}
        user={user}
      />
    );
  }

  // Show Category Selection
  if (gameState === GAME_STATES.CATEGORY_SELECTION) {
    return <CategorySelector onCategorySelect={handleCategorySelect} onBack={backToGameSelection} />;
  }

  // Show deck selection screen
  if (gameState === GAME_STATES.DECK_SELECTION) {
    return <DeckSelector onDeckSelect={handleDeckSelect} onBack={backToCategorySelection} filterCategory={selectedCategory} />;
  }

  // Show Deck Gallery
  if (gameState === GAME_STATES.GALLERY_VIEW) {
    return <DeckGallery selectedDeck={selectedDeck} onBack={backToModeSelection} />;
  }

  if (gameState === GAME_STATES.MODE_SELECTION) {
    const deckInfo = selectedDeck ? DECK_INFO[selectedDeck] : DECK_INFO[DECK_TYPES.US_CITIES];
    return <GameModeSelector onModeSelect={handleModeSelect} onBack={backToDeckSelection} selectedDeck={selectedDeck} deckInfo={deckInfo} />;
  }

  // Get current deck info
  const deckInfo = selectedDeck ? DECK_INFO[selectedDeck] : DECK_INFO[DECK_TYPES.US_CITIES];
  const aiInfo = gameMode === GAME_MODES.HUMAN_VS_COMPUTER ? getAIDifficultyInfo(aiDifficulty) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <GameHeader deckInfo={deckInfo} onBackClick={backToModeSelection} />
          <ScoreBoard 
            gameMode={gameMode}
            player1Wins={player1Wins}
            player2Wins={player2Wins}
            totalGames={totalGames}
            aiInfo={aiInfo}
          />
        </div>

        {/* Game Status */}
        <GameStatus gameState={gameState} statusText={getGameStatusText()} />

        {/* Current Trick Display */}
        <TrickDisplay 
          currentTrick={currentTrick}
          gameMode={gameMode}
          gameState={gameState}
          aiInfo={aiInfo}
          deckColors={selectedDeck ? DECK_INFO[selectedDeck].colors : null}
        />

        {/* Player 2/AI Cards (Top) */}
        <PlayerHand 
          playerType="player2"
          playerName="Player 2"
          cards={player2Cards}
          tricks={player2Tricks}
          selectedCard={selectedCard}
          isPlayable={currentPlayer === PLAYERS.PLAYER2 && gameState === GAME_STATES.PLAYER2_TURN && gameMode === GAME_MODES.HUMAN_VS_HUMAN}
          deckColors={selectedDeck ? DECK_INFO[selectedDeck].colors : null}
          onCardClick={(card) => {
            if (currentPlayer === PLAYERS.PLAYER2 && gameState === GAME_STATES.PLAYER2_TURN && gameMode === GAME_MODES.HUMAN_VS_HUMAN) {
              setSelectedCard(card);
              playCard(card);
            }
          }}
          gameMode={gameMode}
          aiInfo={aiInfo}
        />

        {/* Player 1/Human Cards (Bottom) */}
        <PlayerHand 
          playerType="player1"
          playerName={gameMode === GAME_MODES.HUMAN_VS_COMPUTER ? 'You' : 'Player 1'}
          cards={player1Cards}
          tricks={player1Tricks}
          selectedCard={selectedCard}
          isPlayable={currentPlayer === PLAYERS.PLAYER1 && gameState === GAME_STATES.PLAYER1_TURN}
          deckColors={selectedDeck ? DECK_INFO[selectedDeck].colors : null}
          onCardClick={(card) => {
            if (currentPlayer === PLAYERS.PLAYER1 && gameState === GAME_STATES.PLAYER1_TURN) {
              setSelectedCard(card);
              playCard(card);
            }
          }}
          gameMode={gameMode}
          aiInfo={aiInfo}
          className="mb-8"
        />

        {/* Game Controls */}
        <GameControls gameState={gameState} onStartGame={startNewGame} />
      </div>
    </div>
  );
};

export default CityCardGame;
